import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtDMY } from "@/lib/utils";
import { DateField } from "@/components/DateField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, X, CalendarOff, Clock, Users, Pencil } from "lucide-react";
import { RoleChips } from "@/components/RoleChips";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function SchedulePage() {
  const [shifts, setShifts] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("Monday");
  const [form, setForm] = useState({ start_time: "06:00", roles: [] });
  const [editingId, setEditingId] = useState(null);
  const [roleNames, setRoleNames] = useState([]);
  const [exc, setExc] = useState({ date: "", reason: "", kind: "holiday" });

  const load = useCallback(async () => {
    const [sh, ex, st, ro] = await Promise.all([api.get("/shifts"), api.get("/exceptions"), api.get("/staff"), api.get("/roles")]);
    setShifts(sh.data); setExceptions(ex.data); setRoleNames(ro.data.map((r) => r.name));
    setAvailableCount(st.data.filter((s) => s.status === "available").length);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = (d) => { setDay(d); setEditingId(null); setForm({ start_time: "06:00", roles: [] }); setOpen(true); };
  const openEdit = (s) => { setDay(s.day); setEditingId(s.id); setForm({ start_time: s.start_time, roles: s.roles || [] }); setOpen(true); };

  const addShift = async () => {
    try {
      if (editingId) await api.put(`/shifts/${editingId}`, { day, ...form });
      else await api.post("/shifts", { day, ...form });
      toast.success(editingId ? "Shift updated" : "Shift added");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeShift = async (id) => { await api.delete(`/shifts/${id}`); load(); };

  const addException = async () => {
    if (!exc.date || !exc.reason.trim()) { toast.error("Date and reason required"); return; }
    try {
      await api.post("/exceptions", exc);
      toast.success("Exception added");
      setExc({ date: "", reason: "", kind: "holiday" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeException = async (id) => { await api.delete(`/exceptions/${id}`); load(); };

  return (
    <div className="animate-fade-up">
      <p className="uppercase text-xs tracking-[0.3em] text-muted-foreground mb-2">Roster</p>
      <h1 className="font-head text-4xl sm:text-5xl font-800 tracking-tight leading-none">Weekly Schedule</h1>
      <p className="text-muted-foreground mt-3 mb-4">Add one or more shift start times per day and pick the roles needed. Available staff with those roles are texted.</p>

      <div className="mb-8 inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="available-count-note">
        <Users className="h-4 w-4 text-primary" />
        <span className="text-muted-foreground"><span className="font-semibold text-foreground">{availableCount}</span> available staff. Each shift texts the ones whose role matches.</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {DAYS.map((d) => {
          const dayShifts = shifts.filter((s) => s.day === d);
          return (
            <Card key={d} className="p-4 flex flex-col" data-testid={`day-card-${d}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-head font-700 tracking-tight">{d}</h3>
                <Button variant="ghost" size="icon" onClick={() => openAdd(d)} data-testid={`add-shift-${d}`}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2 flex-1">
                {dayShifts.length === 0 && <p className="text-xs text-muted-foreground py-2">Closed — no shift.</p>}
                {dayShifts.length > 1 && <p className="text-xs text-muted-foreground" data-testid={`shift-count-${d}`}>{dayShifts.length} shifts</p>}
                {dayShifts.map((s) => (
                  <div key={s.id} className="group flex items-start justify-between gap-2 rounded-md bg-muted/40 px-3 py-2" data-testid={`shift-${s.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-primary" />Starts {s.start_time}</p>
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`shift-roles-${s.id}`}>
                        {(s.roles || []).length ? s.roles.map((r) => <Badge key={r} variant="secondary" className="rounded-full text-[10px] font-medium">{r}</Badge>)
                          : <span className="text-[11px] text-destructive">No roles set</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(s)} data-testid={`edit-shift-${s.id}`} className="text-muted-foreground hover:text-foreground transition-colors">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => removeShift(s.id)} data-testid={`delete-shift-${s.id}`}
                        className="text-muted-foreground hover:text-destructive transition-colors">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => openAdd(d)} data-testid={`add-another-shift-${d}`} className="mt-3 gap-1.5 w-full">
                <Plus className="h-3.5 w-3.5" /> {dayShifts.length ? "Add another shift" : "Add shift"}
              </Button>
            </Card>
          );
        })}
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <CalendarOff className="h-5 w-5 text-primary" />
          <h2 className="font-head text-lg font-700 tracking-tight">Exceptions — public holidays & closures</h2>
        </div>
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
              <DateField value={exc.date} onChange={(date) => setExc({ ...exc, date })} testid="exception-date-input" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Reason</Label>
              <Input value={exc.reason} onChange={(e) => setExc({ ...exc, reason: e.target.value })} data-testid="exception-reason-input" className="mt-1.5" placeholder="Queen's Birthday" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Type</Label>
              <Select value={exc.kind} onValueChange={(v) => setExc({ ...exc, kind: v })}>
                <SelectTrigger data-testid="exception-kind-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="holiday">Public holiday</SelectItem>
                  <SelectItem value="closure">Business closure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addException} data-testid="add-exception-button" className="gap-2"><Plus className="h-4 w-4" /> Add exception</Button>
          </div>

          <div className="mt-5 space-y-2">
            {exceptions.length === 0 && <p className="text-sm text-muted-foreground">No exceptions set. The default schedule applies every week.</p>}
            {exceptions.map((x) => (
              <div key={x.id} className="flex items-center justify-between rounded-md border border-border px-4 py-2.5" data-testid={`exception-${x.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium text-sm">{fmtDMY(x.date)}</span>
                  <Badge variant="outline" className="rounded-full border-primary text-primary">{x.kind === "holiday" ? "Public holiday" : "Closure"}</Badge>
                  <span className="text-sm text-muted-foreground">{x.reason}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeException(x.id)} data-testid={`delete-exception-${x.id}`} className="text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shift-dialog">
          <DialogHeader><DialogTitle className="font-head">{editingId ? "Edit shift" : "Add shift"} — {day}</DialogTitle>
            <DialogDescription>Set the start time and the roles needed. You can add as many shifts to a day as you need.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Start time</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="shift-start-input" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Roles needed</Label>
              <div className="mt-2"><RoleChips roles={roleNames} value={form.roles} onChange={(roles) => setForm({ ...form, roles })} testid="shift-roles" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={addShift} data-testid="save-shift-button">{editingId ? "Save shift" : "Add shift"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
